import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './Profile.css';
import { useAuth } from '../contexts/AuthContext';
import JackTravelLinkageFields from './JackTravelLinkageFields';
import { useCommunityLinkagePresets } from '../hooks/useCommunityLinkagePresets';
import { useJackLinkageHandlers } from '../hooks/useJackLinkageHandlers';
import {
  bikeProfileDataSnapshot,
  loadBikeProfilesStore,
  loadMainJackSettings,
  loadUserLinkagePresets,
  newJackBikeProfileId,
  normalizeJackBikeLabel,
  saveBikeProfilesStore,
  saveMainJackSettings,
  setJackOnboardingComplete,
} from '../utils/jackSettings';

/**
 * First bike profile setup: same travel + linkage UI as Settings, same localStorage persistence.
 * Used on /bike-onboarding and inside the Settings welcome modal.
 */
function BikeOnboardingForm({
  variant = 'page',
  onCompleted,
  showSkip = true,
  onSkip,
}) {
  const { user } = useAuth();
  const [main, setMain] = useState(() => loadMainJackSettings());
  const [bikeProfileNameInput, setBikeProfileNameInput] = useState('');
  const [userLinkagePresets, setUserLinkagePresets] = useState(() => loadUserLinkagePresets());
  const { communityPresets, communityListError } = useCommunityLinkagePresets();

  useEffect(() => {
    if (!user?.uid) return;
    setMain(loadMainJackSettings());
    setUserLinkagePresets(loadUserLinkagePresets());
  }, [user?.uid]);

  const commitMain = useCallback((updater) => {
    setMain((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
      saveMainJackSettings(next);
      return next;
    });
  }, []);

  const linkage = useJackLinkageHandlers({
    main,
    commitMain,
    communityPresets,
    userLinkagePresets,
    setUserLinkagePresets,
  });

  const sortedLinkagePresets = useMemo(
    () =>
      [...userLinkagePresets].sort((a, b) =>
        String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }),
      ),
    [userLinkagePresets],
  );

  const setLinkageStatus = linkage.setLinkageStatus;

  const handleSaveAndContinue = useCallback(() => {
    const nameNorm = normalizeJackBikeLabel(bikeProfileNameInput);
    if (!nameNorm) {
      alert('Enter a name for your bike profile.');
      return;
    }
    const profiles = loadBikeProfilesStore().profiles;
    if (profiles.some((p) => normalizeJackBikeLabel(p.name) === nameNorm)) {
      alert('A bike profile with this name already exists (names are not case-sensitive).');
      return;
    }
    const id = newJackBikeProfileId();
    const snap = bikeProfileDataSnapshot(main);
    const newP = { id, name: nameNorm, ...snap };
    const mergedProfiles = [...profiles, newP];
    saveBikeProfilesStore({ profiles: mergedProfiles });
    const nextMain = { ...main, selectedBikeProfileId: id };
    saveMainJackSettings(nextMain);
    setMain(nextMain);
    setJackOnboardingComplete();
    setBikeProfileNameInput('');
    setLinkageStatus(`Saved bike profile “${nameNorm}”.`);
    if (onCompleted) onCompleted();
  }, [bikeProfileNameInput, main, commitMain, setLinkageStatus, onCompleted]);

  const cardClass =
    variant === 'page' ? 'login-container login-container--wide bike-onboarding-card' : 'bike-onboarding-card-modal';

  return (
    <div className={cardClass}>
      <div className="login-header bike-onboarding-header">
        <h2 className="login-title bike-onboarding-title" id="bike-onboard-title">
          Set up your bike
        </h2>
        <p className="bike-onboarding-lead">
          Enter front and rear travel and your rear leverage curve (or pick a preset). This is saved for your account
          on this device (same as Settings)—you can edit it anytime.
        </p>
      </div>

      <div className="jack-settings-stack bike-onboarding-fields" style={{ textAlign: 'left' }}>
        <label className="profile-email jack-settings-label" htmlFor="onboardBikeProfileName">
          Bike profile name
        </label>
        <input
          id="onboardBikeProfileName"
          className="form-input"
          placeholder="e.g. Trail bike, DH rig"
          value={bikeProfileNameInput}
          onChange={(e) => setBikeProfileNameInput(e.target.value)}
        />
      </div>

      <div className="bike-onboarding-travel-block">
        <JackTravelLinkageFields
          idPrefix="onboard"
          main={main}
          updateMain={linkage.updateMain}
          linkageSelectValue={linkage.linkageSelectValue}
          onLinkageSelect={linkage.onLinkageSelect}
          sortedLinkagePresets={sortedLinkagePresets}
          communityPresets={communityPresets}
          communityListError={communityListError}
          handleLinkageCsv={linkage.handleLinkageCsv}
          linkagePresetNameInput={linkage.linkagePresetNameInput}
          setLinkagePresetNameInput={linkage.setLinkagePresetNameInput}
          handleSaveNewLinkagePreset={linkage.handleSaveNewLinkagePreset}
          handleUpdateSavedLinkagePreset={linkage.handleUpdateSavedLinkagePreset}
          linkageSavedSelected={linkage.linkageSavedSelected}
          deleteCurrentLinkagePreset={linkage.deleteCurrentLinkagePreset}
          linkageStatus={linkage.linkageStatus}
        />
      </div>

      <div className="login-actions bike-onboarding-actions">
        {showSkip ? (
          <button type="button" className="login-cancel-btn" onClick={onSkip}>
            Skip for now
          </button>
        ) : null}
        <button type="button" className="primary-btn" onClick={handleSaveAndContinue}>
          Save and continue
        </button>
      </div>
    </div>
  );
}

export default BikeOnboardingForm;
